import React from "react";
import { useSelector } from "react-redux";

import {
  CapabilitiesPicker,
  MultiLineTextField,
  SectionTitle,
  SeparatorBar,
  SubForm,
  SubFormNavigationProps,
} from "../../../components";
import { FormCard } from "../../../components/FormCard";
import { RightSideWrapper } from "../../../components/RightSideWrapper";
import {
  gameConceptInputsMaxLength,
  gameInfoTechnicalCapabilitiesInitialValues,
  useIdXboxGameInformationTechnicalCapabilitiesValidationSchema,
} from "../../../helpers/formValidation";
import { useStrings } from "../../../localization/localizationHelpers";
import { GameInformationGameExpectationsDataType } from "../../../models/forms";
import { AnticipatedCapabilities, XsCapabilities } from "../../../models/gameConceptCapabilities";
import { OnboardingRootState } from "../../../redux/reducers/rootReducer";
import { getIsSavingForm, getSubFormData } from "../../../redux/selectors";
import { GameInformationFormId, GameInformationSubFormsId } from "../GameInformationPageConstants";

const targetXboxSeriesXS = (
  targetPlatforms: Array<{ platform: string; selected: boolean; releaseDate: string }>
) => {
  if (!targetPlatforms) {
    return false;
  }
  const xboxSeriesXS = targetPlatforms.find(
    targetPlatform => targetPlatform.platform === "xboxSeriesXS"
  );
  return xboxSeriesXS?.selected;
};

const TechnicalCapabilitiesSubForm = (props: SubFormNavigationProps) => {
  const [
    allPlatforms,
    anticipatedCapabilities,
    targetNative4k,
    hdr,
    crossGenerationalContent,
    cloudAware,
    xboxSeriesXSVersion,
    xboxSeriesXSVersionSubtitle,
    anticipatedXboxSeriesXSCapabilities,
    rayTracing,
    highFrameRate,
    directStorage,
    seriesXSAdvantageTitle,
  ] = useStrings(
    "allPlatforms",
    "anticipatedCapabilities",
    "targetNative4k",
    "hdr",
    "crossGenerationalContent",
    "cloudAware",
    "xboxSeriesXSVersion",
    "xboxSeriesXSVersionSubtitle",
    "anticipatedXboxSeriesXSCapabilities",
    "rayTracing",
    "highFrameRate",
    "directStorage",
    "seriesXSAdvantageTitle"
  );
  const validationSchema = useIdXboxGameInformationTechnicalCapabilitiesValidationSchema();
  const isSavingFormToCrm = useSelector(getIsSavingForm);
  const gameInfoGameExpectationsSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, GameInformationFormId, GameInformationSubFormsId.GameExpectations)
  ) as GameInformationGameExpectationsDataType;

  return (
    <SubForm
      validateConfig={validationSchema}
      initialValues={gameInfoTechnicalCapabilitiesInitialValues}
      formId={GameInformationFormId}
      subFormId={GameInformationSubFormsId.TechnicalCapabilities}
      {...props}
    >
      <SectionTitle title={allPlatforms} />
      <RightSideWrapper
        rightSideComponent={<FormCard cardId={GameInformationSubFormsId.TechnicalCapabilities} />}
      >
        <CapabilitiesPicker
          name={"anticipatedCapabilities"}
          title={anticipatedCapabilities}
          capabilitiesList={[
            {
              value: AnticipatedCapabilities.targetNative4k,
              label: targetNative4k,
            },
            {
              value: AnticipatedCapabilities.hdr,
              label: hdr,
              disableDetails: true,
            },
            {
              value: AnticipatedCapabilities.crossGenerationalContent,
              label: crossGenerationalContent,
            },
            {
              value: AnticipatedCapabilities.cloudAware,
              label: cloudAware,
            },
          ]}
          isSaving={isSavingFormToCrm}
        />
      </RightSideWrapper>
      {targetXboxSeriesXS(gameInfoGameExpectationsSubFormData?.targetPlatforms) && (
        <>
          <SeparatorBar />
          <SectionTitle title={xboxSeriesXSVersion} subtitle={xboxSeriesXSVersionSubtitle} />
          <CapabilitiesPicker
            name={"xboxSeriesXSVersionCapabilities"}
            title={anticipatedXboxSeriesXSCapabilities}
            capabilitiesList={[
              {
                value: XsCapabilities.rayTracing,
                label: rayTracing,
              },
              {
                value: XsCapabilities.highFrameRate,
                label: highFrameRate,
              },
              {
                value: XsCapabilities.directStorage,
                label: directStorage,
              },
            ]}
            isSaving={isSavingFormToCrm}
          />
          <MultiLineTextField
            name={"xboxSeriesXSAdvantage"}
            title={seriesXSAdvantageTitle}
            isSaving={isSavingFormToCrm}
            maxLengthsByFieldName={gameConceptInputsMaxLength}
          />
        </>
      )}
    </SubForm>
  );
};

export default TechnicalCapabilitiesSubForm;
